import type { AssistantTemplate } from '@owlbert-backend/trpc-client'
import { normalizeHostedBackendBaseUrl } from '@owlbert/core'
import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { IconLibrary } from '@tabler/icons-react'
import React from 'react'
import type { AssistantProposalFields } from '../assistant/assistantProposalFromDescription'

export type AssistantCatalogTrpcClient = {
  assistants: { list: { query: () => Promise<AssistantTemplate[]> } }
}

function templateToProposal(t: AssistantTemplate): AssistantProposalFields {
  const name = t.name?.trim() || 'Assistant'
  const greeting = `Hi! I'm ${name}. How can I help?`
  return {
    name,
    description: (t.description ?? '').trim(),
    instructions: (t.instructions ?? '').trim(),
    greeting,
  }
}

function templateImageUrl(apiBase: string, imagePath: string | undefined): string | null {
  const p = imagePath?.trim()
  if (!p) return null
  if (p.startsWith('http://') || p.startsWith('https://')) return p
  const base = normalizeHostedBackendBaseUrl(apiBase).replace(/\/$/, '')
  return `${base}${p.startsWith('/') ? '' : '/'}${p}`
}

export type AssistantCatalogPickerModalProps = {
  opened: boolean
  onClose: () => void
  /** Browser session tRPC client (`assistants.list` is public). */
  trpcClient: AssistantCatalogTrpcClient | null
  /** API origin for `/static/assistants/…` paths (e.g. `useOwlbertWebAuth().backendUrl`). */
  apiBase: string
  onPick: (proposal: AssistantProposalFields) => void
}

/**
 * Pre-made assistant templates from `assistants.list` (same catalog as desktop / mobile).
 */
export const AssistantCatalogPickerModal: React.FC<AssistantCatalogPickerModalProps> = ({
  opened,
  onClose,
  trpcClient,
  apiBase,
  onPick,
}) => {
  const [rows, setRows] = React.useState<AssistantTemplate[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!opened) {
      setRows([])
      setError(null)
      setLoading(false)
      return
    }
    if (!trpcClient) {
      setRows([])
      setError(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void trpcClient.assistants.list
      .query()
      .then((list) => {
        if (!cancelled) setRows(list)
      })
      .catch((e) => {
        if (!cancelled) setError(String((e as Error)?.message ?? e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [opened, trpcClient])

  return (
    <Modal opened={opened} onClose={onClose} title="Pick from catalog" size="lg" scrollAreaComponent={ScrollArea.Autosize}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Starter assistants from Owlbert. Tap a card to fill the preview — you can still edit after creating.
        </Text>

        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : null}
        {error ? (
          <Text size="sm" c="red">
            {error}
          </Text>
        ) : null}
        {!loading && !error && rows.length === 0 ? (
          <Text size="sm" c="dimmed">
            No templates returned.
          </Text>
        ) : null}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {rows.map((t) => {
            const img = templateImageUrl(apiBase, t.image)
            return (
              <Paper
                key={t.id}
                withBorder
                p="md"
                radius="md"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  onPick(templateToProposal(t))
                  onClose()
                }}
              >
                <Stack gap="xs">
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    {img ? (
                      <Box
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 8,
                          flexShrink: 0,
                          overflow: 'hidden',
                          background: 'var(--mantine-color-dark-5)',
                        }}
                      >
                        <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    ) : (
                      <Box
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 8,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'var(--mantine-color-dark-5)',
                        }}
                      >
                        <IconLibrary size={22} opacity={0.7} />
                      </Box>
                    )}
                    <Title order={5} style={{ flex: 1, minWidth: 0 }} lineClamp={2}>
                      {t.name}
                    </Title>
                  </Group>
                  <Text size="sm" c="dimmed" lineClamp={3}>
                    {t.description}
                  </Text>
                  <Button size="xs" variant="light" fullWidth>
                    Use this template
                  </Button>
                </Stack>
              </Paper>
            )
          })}
        </SimpleGrid>
      </Stack>
    </Modal>
  )
}
